// Layer 4 (UI) — the 24 h control strip on a loop row or a reading row (locked
// comp `process.html` A): ±1σ band, dashed control limits, neutral mean, the
// series in ink and the engine's flagged point in its state tone, the run-rule
// span shaded amber (DESIGN.md § Crimson: no crimson on data). Renders
// ControlStrip.transform.ts; derives nothing.

import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { controlStripGeometry } from './ControlStrip.transform';
import type { ProcessChart } from './Scene.transform';

export function ControlStrip({ chart, width = 124, height = 24 }: { chart: ProcessChart; width?: number; height?: number }) {
  const geometry = controlStripGeometry(chart, width, height);
  if (!geometry) return null;
  const x0 = 2;
  const x1 = width - 2;
  return (
    <svg
      data-control-strip={chart.variableId}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={`${chart.tag ?? chart.variableId} ${chart.windowLabel} against its control limits · ${chart.stateLabel}`}
      className="block shrink-0 overflow-visible"
    >
      {geometry.shade ? (
        <rect x={geometry.shade.x} y={1} width={geometry.shade.width} height={height - 2} fill="color-mix(in srgb, var(--state-warning) 14%, transparent)" />
      ) : null}
      {geometry.sigma ? (
        <rect x={x0} y={geometry.sigma.y} width={x1 - x0} height={geometry.sigma.height} fill="var(--ink-tertiary)" opacity={0.08} />
      ) : null}
      <line x1={x0} x2={x1} y1={geometry.ucl} y2={geometry.ucl} stroke="var(--ink-tertiary)" strokeDasharray="2 2" />
      <line x1={x0} x2={x1} y1={geometry.lcl} y2={geometry.lcl} stroke="var(--ink-tertiary)" strokeDasharray="2 2" />
      <line x1={x0} x2={x1} y1={geometry.mean} y2={geometry.mean} stroke="var(--ink-tertiary)" />
      <polyline fill="none" stroke="var(--ink-primary)" strokeWidth={1.1} strokeLinejoin="round" points={geometry.path} />
      {geometry.flagged.map((point) => (
        <circle key={`${point.x}-${point.y}`} data-flagged-point={point.tone} cx={point.x} cy={point.y} r={2.6} fill={vizToneColor(point.tone)} />
      ))}
      {geometry.last ? <circle cx={geometry.last.x} cy={geometry.last.y} r={1.8} fill="var(--ink-primary)" /> : null}
    </svg>
  );
}
